import { useState, useEffect, useRef } from "react"
import { useNavigate, useLocation } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { LanguageToggle } from "@/components/LanguageToggle"
import { ArrowLeft, Send, ThumbsUp, ThumbsDown, Clock, Bot, User, FileText, Sparkles } from "lucide-react"
import { cn, formatDuration } from "@/lib/utils"
import { api } from "@/services/api"
import { LeadCaptureModal } from "@/components/LeadCaptureModal"
import type { Message } from "@/types"

interface LocationState {
  sessionId: string
  mode: "upload" | "template"
  templateId?: string
}

export function ChatPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useTranslation()
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const { sessionId, mode, templateId } = (location.state as LocationState) || {}

  const [messages, setMessages] = useState<Message[]>([])
  const [inputValue, setInputValue] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const [sessionTime, setSessionTime] = useState(0)
  const [showLeadModal, setShowLeadModal] = useState(false)
  const [hasShownLeadModal, setHasShownLeadModal] = useState(false)
  const [welcomeMessage, setWelcomeMessage] = useState<string>("")

  const getSuggestedQuestions = (mode: "upload" | "template", templateId?: string): string[] => {
    if (mode === "template" && templateId) {
      const keyMap: Record<string, string> = {
        ecommerce: "ecommerce",
        saas: "saas",
        healthcare: "healthcare",
        financial: "financial",
        education: "education",
      }
      const key = keyMap[templateId]
      if (key) {
        return [
          t(`suggestedQuestions.${key}.q1`),
          t(`suggestedQuestions.${key}.q2`),
          t(`suggestedQuestions.${key}.q3`),
          t(`suggestedQuestions.${key}.q4`),
        ]
      }
    }
    return [
      t("suggestedQuestions.upload.q1"),
      t("suggestedQuestions.upload.q2"),
      t("suggestedQuestions.upload.q3"),
      t("suggestedQuestions.upload.q4"),
    ]
  }

  // Validate session
  useEffect(() => {
    if (!sessionId) {
      navigate("/demo/select")
      return
    }

    // Load welcome message based on mode/template
    if (mode === "template" && templateId) {
      const templateNames: Record<string, string> = {
        ecommerce: t("landing.industries.ecommerce"),
        saas: t("landing.industries.saas"),
        healthcare: t("landing.industries.healthcare"),
        financial: t("landing.industries.financial"),
        education: t("landing.industries.education"),
      }
      setWelcomeMessage(
        t("chat.welcomeTemplate", { industry: templateNames[templateId] || templateId })
      )
    } else {
      setWelcomeMessage(t("chat.welcomeUpload"))
    }
  }, [sessionId, mode, templateId, navigate, t])

  // Session timer
  useEffect(() => {
    const timer = setInterval(() => {
      setSessionTime(prev => prev + 1)
    }, 1000)
    return () => clearInterval(timer)
  }, [])

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" })
  }, [messages])

  // Show lead modal after 5 messages or 3 minutes
  useEffect(() => {
    const messageCount = messages.filter(m => m.role === "user").length
    if (!hasShownLeadModal && (messageCount >= 5 || sessionTime >= 180)) {
      setShowLeadModal(true)
      setHasShownLeadModal(true)
    }
  }, [messages, sessionTime, hasShownLeadModal])

  const handleSendMessage = async (content: string) => {
    if (!content.trim() || isLoading) return

    const userMessage: Message = {
      id: Date.now().toString(),
      role: "user",
      content: content.trim(),
      timestamp: new Date().toISOString()
    }

    setMessages(prev => [...prev, userMessage])
    setInputValue("")
    setIsLoading(true)

    try {
      // For demo purposes, simulate a response if API fails
      const result = await api.sendMessage(sessionId, content.trim())

      if (result.success && result.data) {
        setMessages(prev => [...prev, result.data as Message])
      } else {
        // Fallback response for demo
        const fallbackResponse: Message = {
          id: (Date.now() + 1).toString(),
          role: "assistant",
          content: `I understand you're asking about "${content.trim()}". Based on the documentation I've been trained on, I'd be happy to help you with this. However, I'm currently running in demo mode with limited connectivity. In a full implementation, I would provide a detailed, accurate response based on your specific knowledge base.`,
          timestamp: new Date().toISOString(),
          sources: [{ document: "Demo Knowledge Base" }]
        }
        setMessages(prev => [...prev, fallbackResponse])
      }
    } catch {
      // Fallback response
      const fallbackResponse: Message = {
        id: (Date.now() + 1).toString(),
        role: "assistant",
        content: "I apologize, but I'm having trouble connecting to the server right now. This is a demo environment, so some features may be limited. In a production setup, I would provide you with a comprehensive answer based on your uploaded documents.",
        timestamp: new Date().toISOString()
      }
      setMessages(prev => [...prev, fallbackResponse])
    } finally {
      setIsLoading(false)
      inputRef.current?.focus()
    }
  }

  const handleFeedback = async (messageId: string, feedback: "positive" | "negative") => {
    setMessages(prev => prev.map(m =>
      m.id === messageId ? { ...m, feedback } : m
    ))

    await api.submitFeedback(messageId, feedback)
  }

  const handleLeadSubmit = async (data: { name: string; email: string; company: string; phone: string; message: string; consent: boolean }) => {
    const result = await api.captureLead(sessionId, data)

    if (result.success) {
      setShowLeadModal(false)
      // Show success message
      const successMessage: Message = {
        id: Date.now().toString(),
        role: "assistant",
        content: `Thanks for your interest, ${data.name}! Our team will reach out to you at ${data.email} shortly. In the meantime, feel free to continue exploring the demo.`,
        timestamp: new Date().toISOString()
      }
      setMessages(prev => [...prev, successMessage])
    }
  }

  const messageCount = messages.filter(m => m.role === "user").length

  return (
    <div className="h-screen flex flex-col bg-background">
      {/* Header */}
      <header className="flex-shrink-0 glass border-b">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate(mode === "upload" ? "/demo/upload" : "/demo/templates")}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                {t("chat.back")}
              </Button>
              <div className="h-6 w-px bg-border hidden sm:block" />
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 rounded-lg gradient-primary flex items-center justify-center">
                  <Bot className="w-4 h-4 text-white" />
                </div>
                <div className="hidden sm:block">
                  <span className="font-semibold text-foreground">{t("chat.aiSupportDemo")}</span>
                  <span className="text-xs text-muted ml-2">
                    {mode === "upload" ? t("chat.customKnowledge") : t("chat.templateDemo")}
                  </span>
                </div>
              </div>
            </div>

            <div className="flex items-center space-x-4">
              {/* Session Timer */}
              <div className="flex items-center space-x-2 text-sm text-muted">
                <Clock className="w-4 h-4" />
                <span className="font-mono">{formatDuration(sessionTime)}</span>
              </div>

              {/* Language Toggle */}
              <LanguageToggle />

              {/* CTA Button */}
              <Button
                size="sm"
                onClick={() => setShowLeadModal(true)}
                className="hidden sm:flex"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                {t("chat.getThisForBusiness")}
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {/* Welcome Message */}
          {messages.length === 0 && (
            <div className="flex items-start space-x-4 mb-8">
              <div className="w-10 h-10 rounded-full gradient-primary flex items-center justify-center flex-shrink-0">
                <Bot className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1">
                <div className="bg-white rounded-2xl rounded-tl-none p-4 shadow-sm border">
                  <p className="text-foreground">{welcomeMessage}</p>
                </div>

                {/* Suggested Questions */}
                <div className="mt-4 flex flex-wrap gap-2">
                  {getSuggestedQuestions(mode, templateId).map((question, idx) => (
                    <button
                      key={idx}
                      onClick={() => handleSendMessage(question)}
                      className="px-4 py-2 text-sm bg-secondary/10 text-secondary-dark rounded-full hover:bg-secondary/20 transition-colors"
                    >
                      {question}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Message List */}
          {messages.map((message) => (
            <div
              key={message.id}
              className={cn(
                "flex items-start space-x-4 mb-6 message-enter",
                message.role === "user" && "flex-row-reverse space-x-reverse"
              )}
            >
              {/* Avatar */}
              <div className={cn(
                "w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0",
                message.role === "assistant"
                  ? "gradient-primary"
                  : "bg-secondary"
              )}>
                {message.role === "assistant" ? (
                  <Bot className="w-5 h-5 text-white" />
                ) : (
                  <User className="w-5 h-5 text-white" />
                )}
              </div>

              {/* Message Content */}
              <div className={cn(
                "flex-1 max-w-[80%]",
                message.role === "user" && "text-right"
              )}>
                <div className={cn(
                  "inline-block rounded-2xl p-4 text-left",
                  message.role === "assistant"
                    ? "bg-white rounded-tl-none shadow-sm border"
                    : "bg-primary text-white rounded-tr-none"
                )}>
                  <p className="whitespace-pre-wrap">{message.content}</p>
                </div>

                {/* Sources & Feedback */}
                {message.role === "assistant" && (
                  <div className="mt-2 flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      {/* Sources */}
                      {message.sources && message.sources.length > 0 && (
                        <div className="flex items-center text-xs text-muted">
                          <FileText className="w-3 h-3 mr-1" />
                          <span>{t("chat.source", { document: message.sources[0].document })}</span>
                        </div>
                      )}

                      {/* Feedback */}
                      <div className="flex items-center space-x-1">
                        <button
                          onClick={() => handleFeedback(message.id, "positive")}
                          className={cn(
                            "p-1 rounded transition-colors",
                            message.feedback === "positive"
                              ? "text-success bg-success/10"
                              : "text-muted hover:text-foreground"
                          )}
                        >
                          <ThumbsUp className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleFeedback(message.id, "negative")}
                          className={cn(
                            "p-1 rounded transition-colors",
                            message.feedback === "negative"
                              ? "text-red-500 bg-red-50"
                              : "text-muted hover:text-foreground"
                          )}
                        >
                          <ThumbsDown className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}

          {/* Typing Indicator */}
          {isLoading && (
            <div className="flex items-start space-x-4 mb-6">
              <div className="w-10 h-10 rounded-full gradient-primary flex items-center justify-center">
                <Bot className="w-5 h-5 text-white" />
              </div>
              <div className="bg-white rounded-2xl rounded-tl-none p-4 shadow-sm border">
                <div className="flex space-x-1">
                  <div className="typing-dot" />
                  <div className="typing-dot" />
                  <div className="typing-dot" />
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Input Area */}
      <div className="flex-shrink-0 border-t bg-white p-4">
        <div className="max-w-5xl mx-auto">
          {/* Mobile CTA */}
          <div className="sm:hidden mb-4">
            <Button
              onClick={() => setShowLeadModal(true)}
              className="w-full"
              size="sm"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              {t("chat.getThisForBusiness")}
            </Button>
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault()
              handleSendMessage(inputValue)
            }}
            className="flex items-center space-x-4"
          >
            <Input
              ref={inputRef}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder={t("chat.typeMessage")}
              className="flex-1"
              disabled={isLoading}
            />
            <Button
              type="submit"
              disabled={!inputValue.trim() || isLoading}
              className="px-6"
            >
              <Send className="w-4 h-4 mr-2" />
              {t("chat.send")}
            </Button>
          </form>

          <p className="text-xs text-muted text-center mt-2">
            {t("chat.disclaimer")}
          </p>
        </div>
      </div>

      {/* Lead Capture Modal */}
      <LeadCaptureModal
        isOpen={showLeadModal}
        onClose={() => setShowLeadModal(false)}
        onSubmit={handleLeadSubmit}
        sessionDuration={sessionTime}
        messageCount={messageCount}
      />
    </div>
  )
}
